import React from 'react';

import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@repo/videa-components/src/lib/utils';

export interface InteractibleChipProps {
  label: string;
  className?: string;
  variant?: 'default' | 'secondary' | 'outline';
  onDelete?: () => void;
  onClick?: () => void;
}

export const InteractibleChip = ({
  label,
  className,
  variant = 'default',
  onDelete,
  onClick,
}: InteractibleChipProps) => {
  const baseClasses =
    'vai-inline-flex vai-items-center vai-gap-1 vai-pl-3 vai-pr-2 vai-py-1 vai-rounded-full vai-text-sm vai-font-medium';

  const variantClasses = {
    default: 'vai-bg-sapphire-50 vai-text-sapphire-700',
    secondary: 'vai-bg-gray-100 vai-text-gray-700',
    outline: 'vai-border vai-border-gray-300 vai-bg-white vai-text-gray-700',
  };

  const hoverClasses = {
    default: 'hover:vai-bg-sapphire-100 hover:vai-text-sapphire-800',
    secondary: 'hover:vai-bg-gray-200 hover:vai-text-gray-800',
    outline: 'hover:vai-bg-gray-50 hover:vai-text-gray-800',
  };

  const isClickable = Boolean(onClick);
  const clickableClasses = isClickable
    ? `vai-cursor-pointer ${hoverClasses[variant]} vai-transition-colors`
    : '';

  const handleChipClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  return (
    <div
      className={cn(
        baseClasses,
        variantClasses[variant],
        clickableClasses,
        className,
      )}
      onClick={handleChipClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={handleKeyDown}
    >
      <span>{label}</span>
      {onDelete && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete();
          }}
          className="vai-flex vai-items-center vai-justify-center vai-w-5 vai-h-5 vai-rounded-full hover:vai-bg-gray-300 hover:vai-text-gray-700 vai-transition-colors vai-cursor-pointer"
          aria-label={`Remove ${label}`}
        >
          <FontAwesomeIcon
            icon={faTimes}
            className="vai-w-3 vai-h-3 vai-text-gray-500 hover:vai-text-gray-700"
          />
        </button>
      )}
    </div>
  );
};
